import { db } from './index.js';

/**
 * Recherche des templates d'articles pour l'autocomplétion.
 * Résultats triés par pertinence : startsWith d'abord, puis contains.
 * @param {number} storeId
 * @param {string} query
 * @returns {Promise<Array>}
 */
export async function searchArticles(storeId, query) {
  const trimmedQuery = query?.trim().toLowerCase();
  if (!trimmedQuery) {
    return [];
  }

  const templates = await db.articleTemplates.where('storeId').equals(storeId).toArray();

  const startsWith = [];
  const contains = [];

  for (const template of templates) {
    const lowerName = template.name.toLowerCase();
    if (lowerName.startsWith(trimmedQuery)) {
      startsWith.push(template);
    } else if (lowerName.includes(trimmedQuery)) {
      contains.push(template);
    }
  }

  startsWith.sort((a, b) => a.name.localeCompare(b.name));
  contains.sort((a, b) => a.name.localeCompare(b.name));

  return [...startsWith, ...contains];
}

/**
 * Retourne le template d'un article s'il existe déjà dans ce magasin.
 * @param {number} storeId
 * @param {string} name
 * @returns {Promise<Object|undefined>}
 */
export async function getArticleTemplate(storeId, name) {
  const trimmedName = name?.trim();
  if (!trimmedName) {
    return undefined;
  }

  return db.articleTemplates
    .where('[storeId+name]')
    .equals([storeId, trimmedName])
    .first();
}

/**
 * Crée ou met à jour un template d'article (upsert sur storeId+name).
 * @param {number} storeId
 * @param {string} name
 * @param {number|null} aisleId
 * @returns {Promise<number>} - Id du template
 */
export async function saveArticleTemplate(storeId, name, aisleId) {
  const trimmedName = name?.trim();
  if (!trimmedName) {
    throw new Error('Le nom de l\'article est requis');
  }

  const existing = await getArticleTemplate(storeId, trimmedName);

  if (existing) {
    await db.articleTemplates.update(existing.id, { aisleId: aisleId ?? null });
    return existing.id;
  }

  return db.articleTemplates.add({
    name: trimmedName,
    storeId,
    aisleId: aisleId ?? null,
    createdAt: new Date(),
  });
}

/**
 * Retourne tous les items d'une liste.
 * Si storeId != null, tri par ordre de rayon puis par nom.
 * @param {number|null} storeId
 * @returns {Promise<Array>}
 */
/**
 * Récupère les items d'une liste par storeId (null = liste rapide).
 * Dexie n'indexe pas null, d'où l'utilisation de filter pour ce cas.
 */
async function getItemsByStoreId(storeId) {
  if (storeId === null) {
    return db.listItems.filter((item) => item.storeId === null).toArray();
  }
  return db.listItems.where('storeId').equals(storeId).toArray();
}

export async function getListItems(storeId) {
  const items = await getItemsByStoreId(storeId);

  if (storeId === null) {
    return items.sort((a, b) => a.name.localeCompare(b.name));
  }

  const aisles = await db.aisles.where('storeId').equals(storeId).toArray();
  const aisleOrderMap = new Map(aisles.map((a) => [a.id, a.order]));

  return items.sort((a, b) => {
    const orderA = a.aisleId != null ? (aisleOrderMap.get(a.aisleId) ?? Infinity) : Infinity;
    const orderB = b.aisleId != null ? (aisleOrderMap.get(b.aisleId) ?? Infinity) : Infinity;

    if (orderA !== orderB) {
      return orderA - orderB;
    }

    return a.name.localeCompare(b.name);
  });
}

/**
 * Raccourci pour la liste rapide (sans magasin).
 * @returns {Promise<Array>}
 */
export async function getQuickListItems() {
  return getListItems(null);
}

/**
 * Ajoute un item à la liste et met à jour le template correspondant.
 * @param {{ storeId?: number|null, name: string, aisleId?: number|null, quantity?: number, checked?: boolean, serverId?: number|null }} params
 * @returns {Promise<number>} - Id de l'item créé
 */
export async function addListItem({
  storeId = null,
  name,
  aisleId = null,
  quantity = 1,
  checked = false,
  serverId = null,
}) {
  const trimmedName = name?.trim();
  if (!trimmedName) {
    throw new Error('Le nom de l\'article est requis');
  }

  if (quantity != null && (typeof quantity !== 'number' || quantity <= 0)) {
    throw new Error('La quantité doit être un nombre strictement positif');
  }

  const id = await db.listItems.add({
    storeId: storeId ?? null,
    name: trimmedName,
    aisleId: aisleId ?? null,
    quantity: quantity ?? 1,
    checked: checked ?? false,
    addedAt: new Date(),
    serverId: serverId ?? null,
  });

  // Met à jour le template si l'item est associé à un magasin
  if (storeId != null) {
    await saveArticleTemplate(storeId, trimmedName, aisleId ?? null);
  }

  return id;
}

/**
 * Retourne un item par son ID serveur.
 * @param {number} storeId
 * @param {number} serverId
 * @returns {Promise<Object|undefined>}
 */
export async function getItemByServerId(storeId, serverId) {
  return db.listItems
    .where('storeId')
    .equals(storeId)
    .filter((item) => item.serverId === serverId)
    .first();
}

/**
 * Met à jour l'ID serveur d'un item local.
 * @param {number} id
 * @param {number} serverId
 */
export async function setItemServerId(id, serverId) {
  await db.listItems.update(id, { serverId });
}

/**
 * Met à jour un item existant (utilisé pour la sync WebSocket).
 * @param {number} id
 * @param {{ name?: string, aisleId?: number|null, quantity?: number, checked?: boolean }} updates
 */
export async function updateListItem(id, updates) {
  const item = await db.listItems.get(id);
  if (!item) return;

  const patch = {};
  if (updates.name !== undefined) patch.name = updates.name;
  if (updates.aisleId !== undefined) patch.aisleId = updates.aisleId;
  if (updates.quantity !== undefined) patch.quantity = updates.quantity;
  if (updates.checked !== undefined) patch.checked = updates.checked;

  if (Object.keys(patch).length > 0) {
    await db.listItems.update(id, patch);
  }
}

/**
 * Inverse l'état coché/non coché d'un item.
 * @param {number} id
 */
export async function toggleItem(id) {
  const item = await db.listItems.get(id);
  if (!item) {
    throw new Error('Article non trouvé');
  }

  await db.listItems.update(id, { checked: !item.checked });
}

/**
 * Met à jour la quantité d'un item.
 * @param {number} id
 * @param {number} quantity
 */
export async function updateItemQuantity(id, quantity) {
  if (typeof quantity !== 'number' || quantity <= 0) {
    throw new Error('La quantité doit être un nombre strictement positif');
  }

  const item = await db.listItems.get(id);
  if (!item) {
    throw new Error('Article non trouvé');
  }

  await db.listItems.update(id, { quantity });
}

/**
 * Supprime un item de la liste.
 * @param {number} id
 */
export async function deleteItem(id) {
  const item = await db.listItems.get(id);
  if (!item) {
    throw new Error('Article non trouvé');
  }

  await db.listItems.delete(id);
}

/**
 * Supprime tous les items cochés d'une liste.
 * Retourne les items supprimés (pour la sync serveur).
 * @param {number|null} storeId
 * @returns {Promise<Array>} - Items supprimés (avec serverId)
 */
export async function clearCheckedItems(storeId) {
  const checkedItems = await db.listItems
    .filter((item) => item.storeId === storeId && item.checked)
    .toArray();

  const ids = checkedItems.map((item) => item.id);
  if (ids.length > 0) {
    await db.listItems.bulkDelete(ids);
  }

  return checkedItems;
}

/**
 * Supprime tous les items d'une liste.
 * @param {number|null} storeId
 * @returns {Promise<number>} - Nombre d'items supprimés
 */
export async function clearAllItems(storeId) {
  return db.listItems.filter((item) => item.storeId === storeId).delete();
}
